import {
  useContentShareState,
  useRemoteVideoTileState,
  useRosterState,
} from "amazon-chime-sdk-component-library-react";
import { ErrorBoundary } from "@themes/pollinator/ui/ErrorBoundary";
import { Loader } from "@themes/pollinator/ui/Loader";
import { useUserFullname } from "@platform/hooks/useUserFullname";
import { VideoUIGrid } from "./VideoUIGrid";
import { VideoUITileLocalVideoContainer } from "../VideoUITile/VideoUITileLocalVideoContainer";
import { VideoUITileRemoteVideoContainer } from "../VideoUITile/VideoUITileRemoteVideoContainer";
import { extractRoomMemberData } from "@platform/features/video/utils/roomMemberDataUtils";
import { useVideo } from "@platform/features/video/providers/VideoContext";
import { useMeetingManager } from "amazon-chime-sdk-component-library-react";
import { useVideoGridSize } from "../../hooks/useVideoGridSize";

export function VideoUIGridContainer() {
  const userFullname = useUserFullname();

  const { roomMembersData } = useVideo();

  const { gridSize, totalPages, pageSize } = useVideoGridSize();

  const meetingManager = useMeetingManager();

  const { roster } = useRosterState();

  const { tileIdToAttendeeId } = useRemoteVideoTileState();

  const { tiles: contentShareTiles } = useContentShareState();

  const hasContentShare = contentShareTiles && contentShareTiles.length > 0;

  // Get local attendee ID to exclude it from remote tiles
  const localAttendeeId =
    meetingManager.meetingSession?.configuration.credentials?.attendeeId;

  // Create a map of attendee IDs to tile IDs for attendees with video
  const attendeeIdToTileId = new Map<string, number>();
  Object.entries(tileIdToAttendeeId).forEach(([tileId, attendeeId]) => {
    attendeeIdToTileId.set(attendeeId, parseInt(tileId));
  });

  // Get all remote attendees (excluding local attendee)
  const remoteAttendees = Object.entries(roster).filter(([attendeeId]) => {
    return attendeeId !== localAttendeeId;
  });

  const errorResetKey = `${gridSize}-${hasContentShare}`;

  return (
    <ErrorBoundary fallback={<Loader isAbsolute />} resetKey={errorResetKey}>
      <VideoUIGrid size={gridSize} isContentShare={hasContentShare}>
        {remoteAttendees.map(([attendeeId, attendee]) => {
          const tileId = attendeeIdToTileId.get(attendeeId);
          const { displayName, roomMemberData } = extractRoomMemberData(
            attendee,
            roomMembersData
          );

          return (
            <VideoUITileRemoteVideoContainer
              isWithContentShare={hasContentShare}
              key={attendeeId}
              tileId={tileId || 0} // Use 0 as fallback if no tile ID (for attendees without video)
              attendeeId={attendeeId}
              name={displayName}
              roomMemberData={roomMemberData}
            />
          );
        })}
        <VideoUITileLocalVideoContainer
          totalTiles={gridSize}
          nameplate={userFullname}
          isWithContentShare={hasContentShare}
        />
      </VideoUIGrid>
    </ErrorBoundary>
  );
}

export default VideoUIGridContainer;
